<template>
    <app-wrapper>
        <app-header :loan="loan" medium-max-width="max-w-1000">
            <template #top>
                <div class="w-full flex items-center">
                    <div class="flex-auto">
                        Welcome back!
                        <h2 class="mb-0 mt-2 md:mt-4 md:text-4xl">Refinance Your Loan</h2>
                    </div>
                    <div class="flex-initial">
                        <i class="fa fa-refresh text-primary text-5xl"/>
                    </div>
                </div>
            </template>
        </app-header>
        <app-body medium-max-width="max-w-1000">
            <template #text>
                <!-- Current Loan Info -->
                <div class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <p class="text-gray-600 text-sm mb-1">Current Payoff Amount</p>
                            <p class="font-semibold text-gray-900 text-xl">{{ formatMoney(payoff) }}</p>
                        </div>
                        <div>
                            <p class="text-gray-600 text-sm mb-1">Cash Back Available</p>
                            <p class="font-semibold text-green-600 text-xl">{{ canRefinance || advancedRefi ? formatMoney(effectiveMaxCashBack) : '—' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Missed Payment Warning -->
                <div v-if="missedPaymentWarning" class="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
                    <div class="flex items-center mb-2">
                        <i class="fa fa-exclamation-triangle text-yellow-600 text-lg mr-2"></i>
                        <h3 class="text-yellow-800 font-bold text-base mb-0">Missed Payment</h3>
                    </div>
                    <p class="text-yellow-700 text-sm mb-0">
                        You have a missed payment on {{ missedPaymentWarning.latestMissedDate }}.
                        <span v-if="missedPaymentWarning.daysLeft > 0">
                            Continue paying for {{ missedPaymentWarning.daysLeft }} days without missing payments and you'll unlock a lower rate and more cash back!
                        </span>
                    </p>
                </div>

                <!-- Advanced Refi Offer -->
                <div v-if="advancedRefi" class="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-400">
                    <div class="flex items-center mb-3">
                        <i class="fa fa-check-circle text-green-600 text-xl mr-2"></i>
                        <h3 class="text-green-800 font-bold text-lg mb-0">You've unlocked better terms!</h3>
                    </div>
                    <p class="text-green-700 text-sm mb-4">
                        Great progress! You now qualify for more cash back and a longer term — same payment.
                    </p>

                    <!-- Comparison Cards -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <!-- Current Terms Card (disabled, reference only) -->
                        <div class="p-4 rounded-lg border-2 border-gray-200 bg-gray-50 opacity-60">
                            <div class="flex items-center mb-3">
                                <span class="font-semibold text-gray-500">Current Terms</span>
                            </div>
                            <div class="space-y-2">
                                <div class="flex justify-between items-center">
                                    <span class="text-gray-400 text-sm">Payment</span>
                                    <span class="font-semibold text-gray-500">{{ formatMoney(currentPayment) }}</span>
                                </div>
                                <div class="flex justify-between items-center">
                                    <span class="text-gray-400 text-sm">Rate</span>
                                    <span class="font-semibold text-gray-500">{{ formatRate(currentRate) }}</span>
                                </div>
                                <div class="flex justify-between items-center">
                                    <span class="text-gray-400 text-sm">Term</span>
                                    <span class="font-semibold text-gray-500">{{ loan.term }} months</span>
                                </div>
                                <div class="flex justify-between items-center">
                                    <span class="text-gray-400 text-sm">Cash Back</span>
                                    <span class="font-semibold text-gray-500">up to {{ formatMoney(maxCashBack) }}</span>
                                </div>
                            </div>
                        </div>
                        <!-- Better Terms Card (always selected) -->
                        <div class="rounded-lg border-2 border-green-500 relative overflow-hidden">
                            <span class="absolute bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium" style="top: 8px; right: 8px;">Selected</span>
                            <!-- Extra Cash Back Banner -->
                            <div v-if="balanceBonus > 0" class="bg-green-500 px-4 py-2 flex items-center">
                                <i class="fa fa-plus-circle text-white mr-2"></i>
                                <span class="text-white font-semibold text-sm">{{ formatMoney(balanceBonus) }} Extra Cash Back</span>
                            </div>
                            <div class="p-4 bg-green-50">
                                <div class="flex items-center mb-3">
                                    <i class="fa fa-check-circle text-green-500 mr-2"></i>
                                    <span class="font-semibold text-gray-800">Better Terms</span>
                                </div>
                                <div class="space-y-2">
                                    <div class="flex justify-between items-center">
                                        <span class="text-gray-500 text-sm">Payment</span>
                                        <span class="font-bold text-gray-800 text-lg">{{ formatMoney(advancedRefiPayment) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center">
                                        <span class="text-gray-500 text-sm">Rate</span>
                                        <span class="font-semibold text-green-700">{{ formatRate(advancedRefiRate) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center">
                                        <span class="text-gray-500 text-sm">Term</span>
                                        <span class="font-semibold text-green-700">{{ advancedRefiTerm }} months</span>
                                    </div>
                                    <div class="flex justify-between items-center">
                                        <span class="text-gray-500 text-sm">Cash Back</span>
                                        <span class="font-bold text-green-700">up to {{ formatMoney(advancedRefiMaxCashBack) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Phase 2 Teaser for Phase 1 users -->
                <div v-else-if="showPhase2Teaser && !missedPaymentWarning" class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
                    <div class="flex items-center mb-2">
                        <i class="fa fa-star text-yellow-500 text-lg mr-2"></i>
                        <h3 class="text-gray-800 font-bold text-lg mb-0">You're on your way to better terms!</h3>
                    </div>
                    <p class="text-gray-600 text-sm mb-4">
                        Every payment brings you closer to unlocking a lower rate and extra cash back.
                    </p>

                    <!-- Payment Progress -->
                    <div class="mb-4">
                        <div class="flex justify-between items-center mb-1">
                            <span class="text-gray-700 text-sm font-medium">Payment Progress</span>
                            <span class="text-gray-600 text-sm font-semibold">{{ paidPayments }} / {{ phase2TargetPayments }} payments</span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-3">
                            <div
                                class="h-3 rounded-full transition-all"
                                :class="paymentProgressPercent >= 75 ? 'bg-green-500' : 'bg-orange-progress'"
                                :style="{ width: Math.min(100, paymentProgressPercent) + '%' }"
                            ></div>
                        </div>
                        <p v-if="advancedRefiFrozen && advancedRefiFrozenDaysRemaining > 0" class="text-gray-600 text-xs mt-2 mb-0">
                            You've paid enough for better refi options. They unlock in {{ advancedRefiFrozenDaysRemaining }} days.
                        </p>
                        <p v-else-if="paymentsRemaining > 0" class="text-gray-600 text-xs mt-2 mb-0">
                            After {{ paymentsRemaining }} more payments you'll unlock a lower rate and more cash back!
                        </p>
                    </div>

                    <!-- Locked benefits -->
                    <div class="space-y-2">
                        <div class="flex items-center text-gray-500 text-sm">
                            <i class="fa fa-lock mr-2"></i>
                            <span>Lower rate: <span class="line-through">{{ formatRate(currentRate) }}</span> → <strong>{{ formatRate(previewBetterRate) }}</strong></span>
                        </div>
                        <div v-if="previewBalanceBonus > 0" class="flex items-center text-gray-500 text-sm">
                            <i class="fa fa-lock mr-2"></i>
                            <span>Extra cash back: <strong>{{ formatMoney(previewBalanceBonus) }}</strong></span>
                        </div>
                        <div v-if="previewTermMonths > currentTermMonths" class="flex items-center text-gray-500 text-sm">
                            <i class="fa fa-lock mr-2"></i>
                            <span>Term: <span class="line-through">{{ currentTermMonths }} months</span> → <strong>{{ previewTermMonths }} months</strong></span>
                        </div>
                    </div>

                    <div class="mt-4 pt-3 border-t border-gray-200">
                        <p class="text-gray-700 text-sm mb-2">
                            <i class="fa fa-arrow-down mr-1"></i>
                            Want cash back today? You can refinance now at your current rate.
                        </p>
                        <div class="mt-2 p-3 bg-yellow-50 rounded-lg border border-yellow-300 flex items-start">
                            <i class="fa fa-exclamation-triangle text-yellow-600 mr-2 mt-0.5"></i>
                            <p class="text-yellow-800 text-sm mb-0">
                                Note: refinancing resets your progress toward better terms. Your new loan starts from 0%.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Missed payment info note (Case 2) -->
                <div v-if="missedPaymentWarning && !advancedRefi" class="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-300 flex items-start">
                    <i class="fa fa-exclamation-triangle text-yellow-600 mr-2 mt-0.5"></i>
                    <p class="text-yellow-800 text-sm mb-0">
                        Keep making your payments to unlock better refinancing options, or refinance now &mdash; your progress toward better terms will reset.
                    </p>
                </div>

                <!-- Cannot refinance message (Case 2 bug fix: $0 slider) -->
                <div v-if="!canRefinance && !advancedRefi" class="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div class="flex items-center mb-2">
                        <i class="fa fa-info-circle text-blue-600 text-lg mr-2"></i>
                        <h3 class="text-blue-800 font-bold text-base mb-0">Not Enough Cash Back Yet</h3>
                    </div>
                    <p class="text-blue-700 text-sm mb-0">
                        You don't have enough cash back to refinance yet. Keep making your payments and check back soon!
                    </p>
                </div>

                <!-- Standard refi flow (slider + summary) -->
                <template v-if="canRefinance && !advancedRefi">
                    <!-- Info Box -->
                    <div class="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p class="text-blue-800 text-sm">
                            <i class="fa fa-info-circle mr-2"></i>
                            Get cash back from your payments! Choose how much you'd like to receive and we'll add it to your new loan balance.
                        </p>
                    </div>

                    <!-- Error Display -->
                    <div v-if="errors.global" class="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <p class="text-red-800 text-sm">{{ errors.global }}</p>
                    </div>

                    <!-- Cash Back Selection -->
                    <div class="mb-6">
                        <label class="block text-gray-700 font-medium mb-2">
                            Get Your Cash Back
                        </label>
                        <div class="flex items-center space-x-4">
                            <input
                                type="range"
                                v-model.number="cashBackAmount"
                                :min="minCashBack"
                                :max="effectiveMaxCashBack"
                                step="0.01"
                                class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <div class="flex-shrink-0 w-32">
                                <div class="relative">
                                    <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                                    <input
                                        type="number"
                                        v-model.number="cashBackAmount"
                                        :min="minCashBack"
                                        :max="effectiveMaxCashBack"
                                        step="0.01"
                                        class="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                        <span v-if="errors.cash_back_amount" class="text-red-500 text-sm">{{ errors.cash_back_amount }}</span>
                    </div>

                    <!-- New Loan Summary -->
                    <div class="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                        <div class="flex justify-between items-center">
                            <div>
                                <p class="text-gray-600 text-sm mb-1">Your New Loan Amount</p>
                                <p class="font-bold text-green-700 text-2xl">{{ formatMoney(newLoanAmount) }}</p>
                            </div>
                            <div class="text-right">
                                <p class="text-gray-500 text-xs">Payoff: {{ formatMoney(payoff) }}</p>
                                <p class="text-gray-500 text-xs">+ Cash Back: {{ formatMoney(cashBackAmount) }}</p>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Phase 2 Advanced Refi: no slider, apply full benefit (Case 4) -->
                <template v-if="advancedRefi">
                    <!-- Error Display -->
                    <div v-if="errors.global" class="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <p class="text-red-800 text-sm">{{ errors.global }}</p>
                    </div>

                    <!-- New Loan Summary for advanced -->
                    <div class="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                        <div class="flex justify-between items-center">
                            <div>
                                <p class="text-gray-600 text-sm mb-1">Your New Loan Amount</p>
                                <p class="font-bold text-green-700 text-2xl">{{ formatMoney(payoff + advancedRefiMaxCashBack) }}</p>
                            </div>
                            <div class="text-right">
                                <p class="text-gray-500 text-xs">Payoff: {{ formatMoney(payoff) }}</p>
                                <p class="text-gray-500 text-xs">+ Cash Back: {{ formatMoney(advancedRefiMaxCashBack) }}</p>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Submit Button -->
                <div class="mt-8 flex flex-col items-center">
                    <!-- Primary action button -->
                    <button
                        v-if="advancedRefi || canRefinance"
                        @click="submitRefi"
                        :disabled="loading || (!advancedRefi && !isFormValid)"
                        class="bg-orange px-8 py-4 text-white text-lg font-medium rounded-lg flex items-center justify-center hover:bg-orange-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg
                            v-if="loading"
                            class="animate-spin h-5 w-5 text-white mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        <span v-if="!loading">{{ advancedRefi ? 'Apply Better Option' : 'Refinance My Loan' }}</span>
                        <span v-else>Processing...</span>
                    </button>

                    <!-- Return to Portal button -->
                    <a
                        :href="portalUrl"
                        class="mt-4 bg-gray-200 px-8 py-4 text-gray-700 text-lg font-medium rounded-lg flex items-center justify-center hover:bg-gray-300 transition"
                    >
                        Return to Portal
                    </a>

                    <!-- Alternative option message -->
                    <p v-if="!advancedRefi" class="mt-6 text-center text-gray-500 text-sm max-w-md">
                        Keep making your regular payments. As you pay down your loan, you may qualify for even better rates in the future.
                    </p>
                </div>
            </template>
        </app-body>
    </app-wrapper>
</template>

<script>
export default {
    mixins: [require('../../../mixins/InteractsWithGtag').default],

    props: {
        loan: {
            type: Object,
            required: true
        },
        payoff: {
            type: Number,
            required: true
        },
        loanAmount: {
            type: Number,
            required: true
        },
        maxCashBack: {
            type: Number,
            required: true
        },
        paymentAmount: {
            type: Number,
            default: 50
        },
        advancedRefi: {
            type: Boolean,
            default: false
        },
        advancedRefiRate: {
            type: Number,
            default: null
        },
        currentRate: {
            type: Number,
            default: null
        },
        advancedRefiTerm: {
            type: Number,
            default: null
        },
        advancedRefiPayment: {
            type: Number,
            default: null
        },
        currentPayment: {
            type: Number,
            default: null
        },
        advancedRefiMaxCashBack: {
            type: Number,
            default: null
        },
        showPhase2Teaser: {
            type: Boolean,
            default: false
        },
        paybackPct: {
            type: Number,
            default: 0
        },
        targetPct: {
            type: Number,
            default: 0
        },
        previewBetterRate: {
            type: Number,
            default: null
        },
        previewBalanceBonus: {
            type: Number,
            default: 0
        },
        paymentsMade: {
            type: Number,
            default: 0
        },
        totalPayments: {
            type: Number,
            default: 0
        },
        paidPayments: {
            type: Number,
            default: 0
        },
        totalSchedulePayments: {
            type: Number,
            default: 0
        },
        paymentsRemaining: {
            type: [Number],
            default: null,
            validator: value => value === null || typeof value === 'number'
        },
        advancedRefiFrozen: {
            type: Boolean,
            default: false
        },
        advancedRefiAvailableAt: {
            type: String,
            default: ''
        },
        advancedRefiFrozenDaysRemaining: {
            type: Number,
            default: 0
        },
        hasMissedPayments: {
            type: Boolean,
            default: false
        },
        missedPaymentWarning: {
            type: Object,
            default: null
        },
        maxTermMonths: {
            type: Number,
            default: 24
        },
        portalUrl: {
            type: String,
            default: 'https://portal.steppingstoneloans.com'
        },
        previewTermMonths: {
            type: Number,
            default: 24
        },
        currentTermMonths: {
            type: Number,
            default: 12
        }
    },

    data() {
        return {
            gTagPageName: 'introxl-refi-cash-back',
            loading: false,
            cashBackAmount: 0, // Will be set to minCashBack in created()
            selectedRefiOption: this.advancedRefi ? 'advanced' : 'standard',
            errors: {
                cash_back_amount: null,
                global: null,
            }
        }
    },

    computed: {
        effectiveMaxCashBack() {
            if (this.selectedRefiOption === 'advanced' && this.advancedRefiMaxCashBack !== null) {
                return this.advancedRefiMaxCashBack;
            }
            return this.maxCashBack;
        },
        balanceBonus() {
            if (this.advancedRefiMaxCashBack !== null) {
                return this.advancedRefiMaxCashBack - this.maxCashBack;
            }
            return 0;
        },
        paymentSavings() {
            if (this.currentPayment && this.advancedRefiPayment) {
                return this.currentPayment - this.advancedRefiPayment;
            }
            return 0;
        },
        newLoanAmount() {
            return this.payoff + this.cashBackAmount;
        },
        minCashBack() {
            return Math.min(0.01, this.effectiveMaxCashBack);
        },
        phase2TargetPayments() {
            if (this.totalPayments > 0) {
                return this.totalPayments;
            }

            if (this.targetPct > 0 && this.totalSchedulePayments > 0) {
                return Math.ceil(this.totalSchedulePayments * this.targetPct / 100);
            }
            return this.totalSchedulePayments;
        },
        paymentProgressPercent() {
            if (this.phase2TargetPayments <= 0) return 0;
            return Math.round((this.paidPayments / this.phase2TargetPayments) * 100);
        },
        progressPercent() {
            if (this.targetPct <= 0) return 0;
            return Math.min(100, Math.round((this.paybackPct / this.targetPct) * 100));
        },
        canRefinance() {

            // Cannot refinance if no cash back available (payoff >= loanAmount)
            if (this.effectiveMaxCashBack <= 0) return false;
            // Cannot refinance if 0 payments made (nothing to cash back)
            if (this.paidPayments <= 0) return false;

            if (this.hasMissedPayments) {
                return false;
            }

            return true;
        },
        isFormValid() {
            if (!this.canRefinance) return false;
            if (this.cashBackAmount < this.minCashBack || this.cashBackAmount > this.effectiveMaxCashBack) return false;
            return true;
        }
    },

    watch: {
        cashBackAmount(newValue) {
            // Ensure cash back stays within bounds
            if (newValue < this.minCashBack) this.cashBackAmount = this.minCashBack;
            if (newValue > this.effectiveMaxCashBack) this.cashBackAmount = this.effectiveMaxCashBack;
        },
        selectedRefiOption() {
            // Clamp cash back when switching options
            if (this.cashBackAmount > this.effectiveMaxCashBack) {
                this.cashBackAmount = this.effectiveMaxCashBack;
            }
        }
    },

    created() {
        this.gTagEvent('introxl-refi-cash-back', {
            event_category: 'onboarding',
            event_label: 'introxl-refi-cash-back',
        });
    },

    mounted() {
        // For advanced refi (Phase 2), set cash back to max (no slider)
        if (this.advancedRefi) {
            this.cashBackAmount = this.advancedRefiMaxCashBack;
        } else {
            // Initialize cash back to minimum (one payment)
            this.cashBackAmount = this.minCashBack;
        }
    },

    methods: {
        formatMoney(value) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
            }).format(value);
        },

        formatRate(value) {
            if (value === null || value === undefined) return '';
            // Rates come as integers (e.g. 160 = 1.60%, 199 = 1.99%)
            return (value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
        },

        resetErrors() {
            this.errors = {
                cash_back_amount: null,
                global: null,
            };
        },

        submitRefi() {
            this.resetErrors();
            this.loading = true;

            const requestData = {
                cash_back_amount: this.cashBackAmount,
            };

            if (this.advancedRefi) {
                requestData.use_advanced_refi = true;
            }

            axios.post('/introxl/refi-submit/' + this.loan.id, requestData)
                .then(response => {
                    // Redirect to the new loan
                    window.location.href = '/account/loans/' + response.data.loan_id;
                })
                .catch(error => {
                    this.handleError(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        handleError(error) {
            if (error.response && error.response.status === 500) {
                this.errors.global = error.response.data.message || 'An unexpected error occurred. Please try again later.';
            } else if (error.response && error.response.data && error.response.data.errors) {
                for (const key in error.response.data.errors) {
                    if (this.errors.hasOwnProperty(key)) {
                        this.errors[key] = error.response.data.errors[key][0];
                    }
                }
            } else {
                this.errors.global = 'An unexpected error occurred. Please try again later.';
            }
        }
    }
}
</script>

<style scoped>
.bg-orange {
    background-color: #e69e4f;
}

.bg-orange:hover {
    background-color: #d18a39;
}

.bg-orange-dark {
    background-color: #d18a39;
}

.bg-orange-progress {
    background-color: #e69e4f;
}

input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #e69e4f;
    cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #e69e4f;
    cursor: pointer;
    border: none;
}
</style>
